/** Seleccion de empresa con marca. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/authService';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { MobileIcon } from '@/components/MobileIcon';
import { Avatar } from '@/components/Avatar';

export function TenantPage() {
  const navigate = useNavigate();
  const { refresh } = useMobileAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const autoChosenRef = useRef(false);
  const membershipsQuery = useQuery({
    queryKey: ['mobile-auth', 'memberships'],
    queryFn: () => authService.memberships(),
  });

  async function choose(tenantId: string) {
    if (pendingId !== null) return;
    setError(null);
    setPendingId(tenantId);
    try {
      await authService.switchContext(tenantId);
      queryClient.removeQueries({ queryKey: ['tenant'] });
      await refresh();
      navigate('/m', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setPendingId(null);
    }
  }

  useEffect(() => {
    if (membershipsQuery.isPending || membershipsQuery.isError) return;
    const list = membershipsQuery.data ?? [];
    if (list.length === 1 && !autoChosenRef.current && pendingId === null) {
      autoChosenRef.current = true;
      void choose(list[0].tenant_id);
    }
  }, [membershipsQuery.data, membershipsQuery.isPending, membershipsQuery.isError, pendingId]);

  function statusLabel(status: string): string {
    if (status === 'active') return 'Activa';
    if (status === 'inactive') return 'Inactiva';
    return status;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-app safe-area-top safe-area-bottom">
      {/* Marca centrada sobre tarjeta v2 */}
      <div className="flex flex-col items-center px-6 pb-8 pt-16">
        <div className="flex w-full max-w-sm flex-col items-center rounded-card bg-white px-6 py-7 shadow-card">
          <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-light">
            <MobileIcon name="business" className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-text-heading">Elige la empresa</h1>
          <p className="mt-1 text-sm text-text-secondary">Operarás solo en la empresa activa</p>
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{error}</p>
          </div>
        )}

        {membershipsQuery.isPending && <CardListSkeleton count={3} />}

        {membershipsQuery.isError && (
          <EmptyState
            icon="error"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => membershipsQuery.refetch()}
          />
        )}

        {!membershipsQuery.isPending && !membershipsQuery.isError && (membershipsQuery.data ?? []).length === 0 && (
          <EmptyState
            icon="business"
            title="Sin empresas"
            description="Pide acceso a tu administrador."
          />
        )}

        <div className="space-y-3">
          {(membershipsQuery.data ?? []).map((m) => {
            const busy = pendingId === m.tenant_id;
            return (
              <button
                key={m.membership_id}
                type="button"
                onClick={() => void choose(m.tenant_id)}
                disabled={pendingId !== null}
                aria-busy={busy}
                className="flex min-h-[72px] w-full items-center gap-4 rounded-card bg-white p-4 text-left shadow-card transition-all hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Avatar name={m.tenant_name} size={48} />
                <div className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-text-primary">{m.tenant_name}</span>
                  <span className="block truncate text-sm text-text-secondary">
                    {busy ? 'Cambiando de empresa…' : statusLabel(m.status)}
                  </span>
                </div>
                <MobileIcon
                  name={busy ? 'sync' : 'chevron_right'}
                  className={`h-6 w-6 shrink-0 text-text-tertiary ${busy ? 'animate-spin' : ''}`}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
